<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { ref } from 'vue'

import { useAuthStore } from '@/stores/auth-store'
import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { PaginatedResponse } from '@/types/general'
import { Port } from '@/types/cargo-loading'
import { GetDataParameters } from '@/types/table'

import Breadcrumb from '@/components/layout/Breadcrumb.vue'
import CrumbsAndActions from '@/components/layout/CrumbsAndActions.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import ManagePortModal from '@/components/cargo-loading/ManagePortModal.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'

interface FetchPortParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        'start-date': string
        'end-date': string
    }
}

const { t } = useI18n()
const authStore = useAuthStore()

const { data, paginationData, loading, error, refetch } = usePaginatedTable<Port, FetchPortParams>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<Port>>(
            window.route('cl.company.ports.index', {
                company: authStore.companyId,
            }),
            { params: params, signal: abortController.signal },
        )

        return response.data
    },
)

const portModalOpen = ref(false)
const selectedPort = ref<Port>()

function updatePort(port: Port) {
    portModalOpen.value = true
    selectedPort.value = port
}

function portModalClosed() {
    portModalOpen.value = false
    selectedPort.value = undefined
}
</script>

<template>
    <CrumbsAndActions>
        <Breadcrumb :to="{ name: 'cl' }" v-text="t('cargoLoading')" />
        <Breadcrumb current v-text="t('ports')" />

        <template #actions>
            <MyButton plain scheme="primary" size="small" @click="portModalOpen = true">
                <mdi:plus-thick class="mr-1" />
                {{ t('addPort') }}
            </MyButton>
        </template>
    </CrumbsAndActions>

    <RouterView />

    <MyPanel v-if="authStore.companyId" shadow>
        <MyTable
            table-id="cl-ports"
            :rows="data"
            :pagination-data="paginationData"
            :loading="loading"
            :error="error"
            :get-data="refetch"
        >
            <template #actionRow="{ row }">
                <ActionRowItem @click="updatePort(row)">
                    <span><mdi:pencil /></span>
                </ActionRowItem>
            </template>

            <MyTableColumn :name="t('shortName')" property="shortName" />
            <MyTableColumn :name="t('name')" property="name" />
        </MyTable>
    </MyPanel>

    <ManagePortModal
        v-if="portModalOpen"
        v-model="portModalOpen"
        :port="selectedPort"
        @close="portModalClosed"
        @saved="refetch()"
    />
</template>
